"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import LogoWall from "@/app/(public)/shared/components/LogoWall";
import Testimonial from "@/app/(public)/shared/components/Testimonial";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import Image from "next/image";
import travelAgentsGraphic from "@/app/(public)/shared/assets/graphics/travel-agents.png";
import { CheckCircle } from "lucide-react";

export default function TravelAgents() {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  return (
    <main>
      {/* Hero Section */}
      <section className="min-h-[calc(100vh-64px)] lg:h-[calc(100vh-64px)] py-8 mt-[64px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          <div className="flex flex-col justify-center gap-8 md:gap-16 lg:gap-24">
            <div className="flex flex-col gap-6">
              <h1 className="text-h1 text-landing-primary">Bring the human touch to team travel.</h1>
              <p className="text-body-lg text-landing-secondary max-w-530">
                Nowadays collaborates with travel professionals to design seamless, inspiring corporate trips—from sales
                kickoffs to global retreats.
              </p>
              <div className="flex">
                <Button variant="primary" color="primary" size="md" onClick={() => openModal("demo")}>
                  Get Started
                </Button>
              </div>
            </div>

            {/* Static Logo Wall */}
            <LogoWall
              variant="small-static"
              type="enterprise-small"
              trustedByText="Trusted by world class companies"
              className="justify-start"
            />
          </div>

          {/* Right side - Image */}
          <div className="flex items-center justify-center lg:h-full mt-8 lg:mt-0">
            <div className="w-full max-w-lg lg:max-w-none">
              <Image
                src={travelAgentsGraphic}
                alt="Travel agents solution illustration"
                className="w-full h-auto object-contain rounded-2xl"
                priority
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto">
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">Built for travel professionals</h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              Everything you need to coordinate corporate trips that exceed expectations.
            </p>
          </div>

          {/* Content */}
          <div className="space-y-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Get access to exclusive group rates</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Unlock special pricing for group bookings and corporate travel packages. Our partnerships with hotels,
                venues, and service providers ensure you get the best rates for your clients. Leverage our volume
                discounts and established relationships to deliver exceptional value.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Add value to clients with curated event experiences</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Enhance travel packages with memorable activities and local experiences. From team-building activities
                to cultural excursions, we help you create unforgettable moments. Transform standard business trips into
                comprehensive experiences that strengthen teams and create lasting memories.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Work with a dedicated trip planning & logistics team</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Partner with specialists who understand the complexities of corporate travel. Our experienced team
                handles every detail, from initial planning to on-site coordination. Focus on client relationships while
                we manage the intricate logistics that make trips successful.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Earn commission on every event booked</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Generate additional revenue through our competitive commission structure. Transparent pricing and
                reliable payments help you grow your business with confidence. Build a sustainable income stream while
                delivering exceptional value to your clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-12 sm:py-0">
        <div className="">
          <Testimonial variant="single" person="gianna" title="Don't just take our word for it." />
        </div>
      </section>

      {/* Hiring Section */}
      <section className="py-24 ">
        <div className="text-center">
          <h2 className="text-h2 text-landing-primary mb-4">We're hiring!</h2>
          <p className="text-body-lg text-landing-secondary mb-8 max-w-2xl mx-auto">
            If you're a trip designer, account manager, or travel advisor, join our growing team of travel
            professionals.
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              variant="primary"
              color="primary"
              size="md"
              onClick={() =>
                window.open(
                  "https://docs.google.com/document/d/17l4yorBfmDMiFNaUMxL07wYiItIi1E7xGjzTEH75d2E/edit",
                  "_blank"
                )
              }
            >
              Apply Here
            </Button>
            <Button variant="outline" color="neutral" size="md" onClick={() => openModal("demo")}>
              Schedule Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Travel Agent Solutions"
        subtitle="Schedule a call to learn how we can streamline your group travel coordination"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Book your travel agent demo"
      />
    </main>
  );
}
